import * as Schema from "effect/Schema";
import { ConnectAccount, type ConnectAccountStatus } from "@afar-research/contracts";
import type { ConnectTokens } from "./signIn.ts";
import {
  ConnectError,
  connectOrigin,
  requireConnect,
  type ConnectFetch,
} from "@afar-research/shared/connect";

/**
 * What custody holds for one account.
 *
 * Everything the access token needs to outlive itself is optional, because a credential written
 * before #1008 has none of it and must keep working: an access token with no refresh token is a
 * credential that will expire and cannot be renewed, which is a fact to record rather than a file
 * to reject. `reauthenticationRequired` is set only once the identity service has actually refused
 * the grant, so the app can say the credential is spent instead of insisting it is fine.
 */
const Credential = Schema.Struct({
  accountToken: Schema.String,
  refreshToken: Schema.optionalKey(Schema.String),
  /** Epoch milliseconds. Absent means unknown, which is treated as "use it and find out". */
  accessTokenExpiresAt: Schema.optionalKey(Schema.Number),
  reauthenticationRequired: Schema.optionalKey(Schema.Boolean),
  installation: Schema.Struct({
    installationId: Schema.String,
    credential: Schema.String,
    privateKey: Schema.String,
    publicKey: Schema.String,
  }),
});

/**
 * How long before expiry a token is renewed anyway, so a call does not set out with a credential
 * that dies in flight.
 */
const REFRESH_LEEWAY_MS = 60_000;

/**
 * A grant as custody stores it. One place translates between the identity service's names and
 * this file's, so a grant that arrives with no expiry or no refresh token is recorded as holding
 * neither rather than as holding `undefined`.
 */
const storedTokens = (tokens: ConnectTokens) => ({
  accountToken: tokens.accessToken,
  ...(tokens.refreshToken === undefined ? {} : { refreshToken: tokens.refreshToken }),
  ...(tokens.expiresAt === undefined ? {} : { accessTokenExpiresAt: tokens.expiresAt }),
});
const SavedAccount = Schema.Struct({ ...Credential.fields, profile: ConnectAccount });
const Accounts = Schema.Struct({
  accounts: Schema.Array(SavedAccount),
  activeAccountId: Schema.optionalKey(Schema.String),
});
const decodeAccounts = Schema.decodeUnknownSync(Schema.fromJsonString(Accounts));
const encodeAccounts = Schema.encodeSync(Schema.fromJsonString(Accounts));
const UserInfo = Schema.Struct({
  sub: Schema.String,
  name: Schema.optionalKey(Schema.String),
  email: Schema.optionalKey(Schema.String),
});
const decodeUserInfo = Schema.decodeUnknownSync(UserInfo);

/** Who is signed in according to a stored account record, ignoring the tokens renewal rewrites. */
const signedInIdentity = (stored: string | null): string | null => {
  if (stored === null) return null;
  try {
    const saved = decodeAccounts(stored);
    const active = saved.accounts.find((item) => item.profile.id === saved.activeAccountId);
    return active ? `${active.profile.id}\n${active.installation.installationId}` : null;
  } catch {
    return null;
  }
};

/**
 * Whether a write to the stored accounts changed who is signed in, rather than only renewing the
 * active account's tokens. Another window renews on its own schedule; only a sign-in, sign-out or
 * account switch there is news to this one.
 */
export const connectAccountChanged = (previous: string | null, next: string | null) =>
  signedInIdentity(previous) !== signedInIdentity(next);

/** Only public identity fields cross the native bridge. Tokens and metadata stay in custody. */
export async function readConnectProfile(fetch: ConnectFetch, issuer: string, token: string) {
  const origin = connectOrigin(issuer);
  const response = await fetch(
    new Request(`${origin}/oauth/userinfo`, {
      headers: { authorization: `Bearer ${token}` },
      redirect: "error",
      signal: AbortSignal.timeout(10000),
    }),
  );
  requireConnect(response.ok, "account_profile_unavailable", 503);
  const profile = decodeUserInfo(await response.json());
  return {
    id: `${origin}/${profile.sub}`,
    issuer: origin,
    ...(profile.name ? { name: profile.name } : {}),
    ...(profile.email ? { email: profile.email } : {}),
  };
}

/** Call within the platform's operation queue. Persistence precedes active-account changes. */
export function connectAccounts(input: {
  read: () => Promise<string | undefined>;
  write: (value: string) => Promise<void>;
  changed: () => Promise<void>;
  /**
   * How to renew a spent access token, or absent where this platform cannot.
   *
   * One optional bundle rather than two optional fields: renewal needs both a way to exchange the
   * refresh token and a clock to know when to, and a half-configured custody that silently never
   * renews is the bug this whole change exists to remove. `refresh` must throw a 401 when the
   * grant is refused and a 503 when the service could not answer — custody spends the credential
   * for the first and keeps it for the second.
   */
  renewal?: {
    refresh: (refreshToken: string) => Promise<ConnectTokens>;
    /** The platform's clock, never this module's. */
    now: () => Promise<number>;
  };
}) {
  const read = async (): Promise<typeof Accounts.Type> => {
    const raw = await input.read();
    return raw ? decodeAccounts(raw) : { accounts: [] };
  };
  const save = async (next: typeof Accounts.Type) => {
    await input.changed();
    await input.write(encodeAccounts(next));
  };
  /**
   * The renewal in flight, so callers that arrive together on an expired token wait on one
   * exchange rather than racing several. Each would otherwise spend the same refresh token, and a
   * service that rotates it on use invalidates the copies behind the first.
   */
  /**
   * Serializes custody's own read-modify-write.
   *
   * The platform queue covers the operations a user starts; a renewal starts by itself, in the
   * middle of whatever call discovered the token was spent, and on platforms where that call is
   * not serialized it would otherwise read and rewrite the whole account file underneath an add
   * or a switch. Nothing inside this lock calls outward, so it cannot deadlock against the
   * platform queue holding it.
   */
  let mutating: Promise<unknown> = Promise.resolve();
  const mutate = <A>(operation: () => Promise<A>): Promise<A> => {
    const result = mutating.then(operation, operation);
    mutating = result.catch(() => undefined);
    return result;
  };
  const renewing = new Map<string, Promise<string>>();
  const renew = async (active: typeof SavedAccount.Type, refreshToken: string): Promise<string> => {
    const result = await input.renewal!.refresh(refreshToken).catch(async (cause: unknown) => {
      // Only a refusal is a verdict on the credential. Anything else leaves it alone to be
      // retried, because an unreachable identity service is our problem, not the user's (#1008).
      if (cause instanceof ConnectError && cause.status === 401)
        await markReauthentication(active.profile.id, true);
      throw cause;
    });
    await patchAccount(active.profile.id, {
      ...storedTokens(result),
      reauthenticationRequired: false,
    });
    return result.accessToken;
  };
  /** Addressed by account rather than by "whichever is active", which can change under us. */
  const patchAccount = (accountId: string | undefined, patch: Partial<typeof Credential.Type>) =>
    mutate(async () => {
      const saved = await read();
      await input.write(
        encodeAccounts({
          ...saved,
          accounts: saved.accounts.map((item) =>
            item.profile.id === accountId ? { ...item, ...patch } : item,
          ),
        }),
      );
    });
  const markReauthentication = (accountId: string | undefined, required: boolean) =>
    patchAccount(accountId, { reauthenticationRequired: required });
  return {
    async load() {
      const saved = await read();
      const active = saved.accounts.find((item) => item.profile.id === saved.activeAccountId);
      requireConnect(active, "account_sign_in_required", 401);
      return active;
    },
    /**
     * A usable access token for the active account, renewed first if the stored one is spent.
     *
     * Every authenticated caller goes through here rather than reading `load().accountToken`,
     * which is what let a long-lived installation keep presenting a token Connect had stopped
     * accepting (#1008).
     */
    async accessToken(): Promise<string> {
      const saved = await read();
      const active = saved.accounts.find((item) => item.profile.id === saved.activeAccountId);
      requireConnect(active, "account_sign_in_required", 401);
      requireConnect(!active.reauthenticationRequired, "account_sign_in_required", 401);
      // Nothing to renew with is not a failure: the stored token is all this account has, and it
      // may still be accepted. Let the route that refuses it be the one that says so.
      if (
        !input.renewal ||
        active.refreshToken === undefined ||
        active.accessTokenExpiresAt === undefined
      )
        return active.accountToken;
      const expiring =
        active.accessTokenExpiresAt - REFRESH_LEEWAY_MS <= (await input.renewal.now());
      if (!expiring) return active.accountToken;
      // Keyed by account: a renewal in flight for one account must never answer for another,
      // which an account switch between the two calls would otherwise make possible.
      const accountId = active.profile.id;
      const pending =
        renewing.get(accountId) ??
        renew(active, active.refreshToken).finally(() => renewing.delete(accountId));
      renewing.set(accountId, pending);
      return pending;
    },
    /**
     * Renews regardless of what the stored expiry claims, for a token a route has just refused.
     *
     * Proactive renewal only fires when custody knows when the token dies. A grant issued with no
     * `expires_in`, or a clock that disagrees with the server's, leaves custody believing a dead
     * token is fine — so the refusal itself has to be able to trigger one renewal. Returns
     * undefined when there is nothing to renew with, which is the only case the caller should
     * read as "this account must sign in again". A grant the identity service already refused is
     * nothing to renew with: asking again would only be refused again, on every call, until the
     * user signs in.
     */
    async renewAccessToken(): Promise<string | undefined> {
      const saved = await read();
      const active = saved.accounts.find((item) => item.profile.id === saved.activeAccountId);
      if (
        !active ||
        active.reauthenticationRequired ||
        !input.renewal ||
        active.refreshToken === undefined
      )
        return undefined;
      const accountId = active.profile.id;
      const pending =
        renewing.get(accountId) ??
        renew(active, active.refreshToken).finally(() => renewing.delete(accountId));
      renewing.set(accountId, pending);
      return pending;
    },
    /** Records that the identity service refused this account's credential. */
    async markReauthenticationRequired() {
      const saved = await read();
      await markReauthentication(saved.activeAccountId, true);
    },
    /** Reads only what this device saved; it never waits for the identity service. */
    async status(): Promise<ConnectAccountStatus> {
      const saved = await read();
      const active = saved.accounts.find((item) => item.profile.id === saved.activeAccountId);
      return {
        state: !active
          ? "signed-out"
          : active.reauthenticationRequired
            ? "needs-sign-in"
            : "signed-in",
        accounts: saved.accounts.map((item) => item.profile),
        ...(active ? { activeAccountId: active.profile.id } : {}),
        ...(active?.reauthenticationRequired
          ? {
              message:
                "Your AFAR account session has expired. Sign in again to send feedback and reach your computers.",
            }
          : {}),
      };
    },
    async switchAccount(id: string) {
      const saved = await read();
      const selected = saved.accounts.find((item) => item.profile.id === id);
      requireConnect(selected, "account_not_saved", 404);
      await save({ ...saved, activeAccountId: selected.profile.id });
    },
    async add(inputAccount: {
      tokens: ConnectTokens;
      profile?: ConnectAccount;
      isRegistered: (installationId: string) => Promise<boolean>;
      register: () => Promise<typeof Credential.Type.installation>;
      revoke: (credential: string) => Promise<void>;
    }) {
      const saved = await read();
      const previous = saved.accounts.find((item) => item.profile.id === inputAccount.profile?.id);
      const reusable =
        previous && (await inputAccount.isRegistered(previous.installation.installationId));
      const installation = reusable ? previous.installation : await inputAccount.register();
      const profile = inputAccount.profile ?? { id: installation.installationId };
      try {
        await save({
          accounts: [
            ...saved.accounts.filter((item) => item.profile.id !== profile.id),
            {
              ...storedTokens(inputAccount.tokens),
              // A fresh sign-in is exactly what the demand was asking for.
              reauthenticationRequired: false,
              installation,
              profile,
            },
          ],
          activeAccountId: profile.id,
        });
      } catch (cause) {
        if (!reusable) await inputAccount.revoke(installation.credential).catch(() => undefined);
        throw cause;
      }
    },
    /** Confirmed revocation must survive cleanup failure and process restart. */
    async removeRevokedActive() {
      const saved = await read();
      await input.write(
        encodeAccounts({
          accounts: saved.accounts.filter((item) => item.profile.id !== saved.activeAccountId),
        }),
      );
    },
    async removeActive() {
      const saved = await read();
      await save({
        accounts: saved.accounts.filter((item) => item.profile.id !== saved.activeAccountId),
      });
    },
  };
}

/**
 * Runs an authenticated Connect call, renewing once if Connect refuses the token.
 *
 * Proactive renewal handles the tokens custody knows the lifetime of. This handles the rest: a
 * grant issued without an expiry, a clock that disagreed with the server's, or a token revoked
 * before it was due to die. Only after a renewal has been tried and found impossible is the
 * account told to sign in again — marking it on the first refusal would discard live grants over
 * clock skew (#1008).
 *
 * A renewal the identity service refuses marks the account itself and throws; one it could not
 * answer throws untouched, because an unreachable service is no verdict on the credential.
 */
export async function withConnectAccountToken<A>(
  accounts: Pick<
    ReturnType<typeof connectAccounts>,
    "accessToken" | "renewAccessToken" | "markReauthenticationRequired"
  >,
  operation: (token: string) => Promise<A>,
): Promise<A> {
  const refused = (cause: unknown) => cause instanceof ConnectError && cause.status === 401;
  try {
    return await operation(await accounts.accessToken());
  } catch (cause) {
    if (!refused(cause)) throw cause;
    const renewed = await accounts.renewAccessToken();
    if (renewed === undefined) {
      await accounts.markReauthenticationRequired();
      throw cause;
    }
    try {
      return await operation(renewed);
    } catch (retry) {
      // A freshly renewed token refused again is the account, not the clock.
      if (refused(retry)) await accounts.markReauthenticationRequired();
      throw retry;
    }
  }
}
